import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Drill-down modal for the Sales Emails to Leads dashboard panel.
// Given a (month, fuel_brand) pair, fetches the deduplicated list of
// lead records that received an email-type note in that period and
// renders them as a clickable, sortable table.
//
// Click a row → navigate to the lead's detail page.
// Click a column header → sort the table by that column. Click again
// to flip the direction. Default sort is by last_email_date desc so
// the most recently contacted leads sit on top when the modal opens.

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function leadName(lead) {
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ');
  return lead.company || name || `Lead #${lead.id}`;
}

// Column registry — each column has its sort key and a value-getter
// that pulls the comparable value off a lead row. Putting it in one
// table keeps the header definitions and the sort logic in sync.
const COLUMNS = [
  { key: 'lead',             label: 'Lead',       align: 'left',  sortable: true,
    // Lead cell now includes contact name + phone + email inline
    // (Dave's mockup, 2026-05-27). Phone/Email standalone columns
    // were dropped to save horizontal space on the wide modal —
    // those data points are still here, just rendered as sub-lines
    // under the company. Sort key is still company name.
    getValue: (l) => leadName(l).toLowerCase() },
  { key: 'city',             label: 'City',       align: 'left',  sortable: true,
    getValue: (l) => (l.city || '').toLowerCase() },
  { key: 'email_count',      label: 'Emails',     align: 'right', sortable: true,
    getValue: (l) => l.email_count || 0 },
  { key: 'first_email_date', label: 'First Email', align: 'left', sortable: true,
    getValue: (l) => l.first_email_date || '' },
  { key: 'last_email_date',  label: 'Last Email',  align: 'left', sortable: true,
    getValue: (l) => l.last_email_date || '' },
  // Engagement columns added 2026-05-27. Backend (getSalesEmailsLeads)
  // now aggregates opened_at + opened_any + opens_count + bounced_any
  // + delivered_any so we can render a date, a count, and a badge.
  { key: 'last_opened_date', label: 'Opened',    align: 'left',  sortable: true,
    getValue: (l) => l.last_opened_date || '' },
  { key: 'opens_count',      label: 'Opens',     align: 'right', sortable: true,
    getValue: (l) => l.opens_count || 0 },
  { key: 'status',           label: 'Status',    align: 'left',  sortable: true,
    // Sort by severity: BOUNCED (worst, ranked first when descending),
    // SENT (lowest engagement), DELIVERED, OPENED (best). Encoded as
    // integers so the existing sort comparator orders them sensibly.
    getValue: (l) => l.bounced_any ? 4 : l.opened_any ? 3 : l.delivered_any ? 2 : 1 }
];

export default function SalesEmailsLeadsModal({ open, row, onClose }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sort state: which column is the active sort, and ascending vs
  // descending. Default: most recent activity first.
  const [sortBy, setSortBy] = useState('last_email_date');
  const [sortDir, setSortDir] = useState('desc');

  // Free-text search across lead name, company, phone, email and city.
  // Filters the table in addition to the column sort.
  const [query, setQuery] = useState('');

  const navigate = useNavigate();

  // Fetch the drill-down list whenever the modal opens with a new row.
  // Cancel pattern guards against a stale response landing after the
  // user has already closed or opened a different cell.
  useEffect(() => {
    if (!open || !row) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLeads([]);
    // Reset to the default sort each time the modal opens — keeps the
    // most-recent-contact-on-top mental model intact across openings.
    setSortBy('last_email_date');
    setSortDir('desc');
    setQuery('');
    axios.get('/api/dashboard/sales-emails-leads', {
      params: { month: row.month, fuel_brand: row.fuel_brand }
    })
      .then((res) => {
        if (!cancelled) setLeads(res.data?.leads || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, row]);

  // Escape key closes the modal — matches the pattern in QuickNoteModal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Filter + sort the leads. Filter happens first (cheap substring match
  // across the searchable fields), then the result is sorted by the
  // active column. Memoised so we don't re-run on every keystroke render.
  const visibleLeads = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Filter step. Empty query = no filtering.
    const filtered = !q ? leads : leads.filter((l) => {
      const haystack = [
        l.company, l.first_name, l.last_name,
        l.phone_main, l.email1, l.city, l.province_state
      ]
        .map((v) => (v == null ? '' : String(v).toLowerCase()))
        .join(' | ');
      return haystack.includes(q);
    });

    // Sort step.
    const col = COLUMNS.find((c) => c.key === sortBy);
    if (!col) return filtered;
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = col.getValue(a);
      const bv = col.getValue(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      // Stable tiebreak: alphabetical by lead name.
      const an = leadName(a).toLowerCase();
      const bn = leadName(b).toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return  1;
      return 0;
    });
  }, [leads, query, sortBy, sortDir]);

  // Header click handler. Clicking the same column flips direction;
  // clicking a different column starts that column in its sensible
  // default (date and numeric columns default to descending so the
  // biggest/latest values are on top; text columns default to asc).
  function onHeaderClick(col) {
    if (!col.sortable) return;
    if (col.key === sortBy) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      const isDateOrNumber = col.key.endsWith('_date') || col.key === 'email_count' || col.key === 'opens_count' || col.key === 'status';
      setSortBy(col.key);
      setSortDir(isDateOrNumber ? 'desc' : 'asc');
    }
  }

  if (!open || !row) return null;

  function go(leadId) {
    onClose();
    navigate(`/leads/${leadId}`);
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="modal-card modal-extra-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Sales emails — lead list"
        // Wider than the default 720px modal — this table has 7 columns
        // (now incl. First and Last Email dates) and benefits from
        // horizontal room so dates and addresses don't wrap awkwardly.
        style={{ maxWidth: 1200 }}
      >
        <header className="modal-head">
          <h3>Sales Emails — {row.fuel_brand} — {row.month_label}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="modal-body">
          {error && <p className="error">{error}</p>}
          {loading && <p className="modal-sub">Loading…</p>}

          {!loading && !error && leads.length === 0 && (
            <p className="panel-empty">No leads found for this month + brand.</p>
          )}

          {!loading && leads.length > 0 && (
            <>
              <p className="modal-sub">
                {leads.length} unique lead{leads.length === 1 ? '' : 's'} received
                {' '}{leads.reduce((s, l) => s + l.email_count, 0)} email{leads.reduce((s, l) => s + l.email_count, 0) === 1 ? '' : 's'} in {row.month_label}.
                {' '}Click a column header to sort.
              </p>

              {/* Search box — filters the table by substring match across
                  company, contact name, phone, email and city. Auto-
                  focused so Dave can just start typing the moment the
                  modal opens. */}
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <input
                  type="search"
                  className="search-input"
                  placeholder="Search company, name, phone, email, city…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  style={{ width: '100%' }}
                />
                {query && (
                  <p className="modal-sub" style={{ marginTop: 'var(--space-2)' }}>
                    Showing {visibleLeads.length} of {leads.length} leads.
                  </p>
                )}
              </div>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    {COLUMNS.map((col) => {
                      const isActive = col.key === sortBy;
                      const arrow = isActive ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';
                      return (
                        <th
                          key={col.key}
                          onClick={() => onHeaderClick(col)}
                          style={{
                            textAlign: col.align,
                            whiteSpace: 'nowrap',
                            cursor: col.sortable ? 'pointer' : 'default',
                            userSelect: 'none'
                          }}
                          title={col.sortable ? 'Click to sort' : undefined}
                        >
                          {col.label}{arrow}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      onClick={() => go(lead.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {/* Three-line stacked cell — company / contact·phone /
                            Email <addr>. Replaces the old three columns
                            (Lead, Phone, Email) so the modal is narrower
                            and the contact info reads at a glance. Phone
                            + email stay as tel:/mailto: links so a click
                            still triggers the OS handler. */}
                        <div className="cell-primary">{leadName(lead)}</div>
                        {(lead.first_name || lead.last_name || lead.phone_main) && (
                          <div className="cell-subtext">
                            {[lead.first_name, lead.last_name].filter(Boolean).join(' ')}
                            {(lead.first_name || lead.last_name) && lead.phone_main && ' · '}
                            {lead.phone_main && (
                              <a
                                href={`tel:${lead.phone_main}`}
                                onClick={(e) => e.stopPropagation()}
                                className="cell-mono"
                              >
                                {lead.phone_main}
                              </a>
                            )}
                          </div>
                        )}
                        {lead.email1 && (
                          <div className="cell-subtext">
                            Email{' '}
                            <a
                              href={`mailto:${lead.email1}`}
                              onClick={(e) => e.stopPropagation()}
                              className="cell-mono"
                            >
                              {lead.email1}
                            </a>
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {lead.city || '—'}
                        {lead.province_state && `, ${lead.province_state}`}
                      </td>
                      <td className="cell-mono" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {lead.email_count}
                      </td>
                      <td className="cell-mono" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(lead.first_email_date)}
                      </td>
                      <td className="cell-mono" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(lead.last_email_date)}
                      </td>
                      <td className="cell-mono" style={{ whiteSpace: 'nowrap' }}>
                        {lead.last_opened_date ? formatDate(lead.last_opened_date) : '—'}
                      </td>
                      <td className="cell-mono" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {lead.opens_count || 0}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* Status badge from aggregate fields. Bounced wins
                            (an open after a bounce still flags as risky);
                            then opened/delivered/sent in descending order
                            of engagement. Same colour scheme as the Email
                            Tracking page status column. */}
                        {lead.bounced_any   ? <span className="badge badge-red">BOUNCED</span>
                         : lead.opened_any  ? <span className="badge badge-green">OPENED</span>
                         : lead.delivered_any ? <span className="badge badge-teal">DELIVERED</span>
                         :                    <span className="badge badge-grey">SENT</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
